import React, { useState, useCallback, useContext } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Typography,
    IconButton,
    Avatar,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Skeleton // Import Skeleton
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Icon for errors
import { WalletsContext } from '../../state/context/wallets.context';
import { transformToBTC } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/constants';
import WithdrawDialog from './Withdraw.component';

// API call function
export const fetchWalletDetailsAPI = (address) => {
    // const urlArray = [`https://api.odin.fun/v1/user/${ address }/stats`, `https://api.odin.fun/v1/user/${ address }`];
    // const fetchPromises = urlArray.map(url => fetch(url,{
    //     headers:{
    //         'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/114.0.0.0 Safari/537.36',
    //     }
    // }).then(res => res.json()));
    // return Promise.all(fetchPromises)
    //     .then(([stats, user]) => {
    //         // Assuming the API returns an object with balance and lastTx properties
    //         return {
    //             balance: stats.data.btc,
    //             depositAddress: user.btc_deposit_address,
    //             ...user
    //         };
    //     })
    //     .catch((error) => {
    //         throw new Error('Failed to fetch wallet details: ' + error.message);
    //     });

    return fetch(`${ API_BASE_URL }/user-data?userId=${ address }`).then((response) => {
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return response.json();
    }
    ).then((data) => {
        // Assuming the API returns an object with balance and lastTx properties
        return {
            balance: data.statsResponse.data.btc,
            depositAddress: data.userResponse.btc_deposit_address,
            ...data
        };
    })
        .catch((error) => {
            throw new Error('Failed to fetch wallet details: ' + error.message);
        });
};

function WalletCard({ wallet }) { // JS version
    const { deleteWallet } = useContext(WalletsContext);
    const [open, setOpen] = useState(false);

    const [isExpanded, setIsExpanded] = useState(false); // Track accordion expansion state
    const [detailsData, setDetailsData] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [error, setError] = useState(null);
    const [showPrivateKey, setShowPrivateKey] = useState(false);
    const navigate = useNavigate();

    const handleTogglePrivateKeyVisibility = () => {
        setShowPrivateKey(!showPrivateKey);
    };
    const handleDelete = () => {
        deleteWallet(wallet._id);
    };

    // Use useCallback to memoize the fetch function if needed, though simple here
    const fetchDetails = async () => {
        // Prevent fetching if already loading or successful
        if (loadingStatus === 'loading' || loadingStatus === 'success') {
            return;
        }

        setLoadingStatus('loading');
        setError(null); // Clear previous errors
        try {
            // Use the actual API call here, passing wallet.address or wallet.id
            const data = await fetchWalletDetailsAPI(wallet.userId);
            setDetailsData(data);
            setLoadingStatus('success');
        } catch (err) {
            setError(err.message || 'An unexpected error occurred.');
            setLoadingStatus('error');
        }
    };


    const handleAccordionChange = (event, expanded) => {
        setIsExpanded(expanded);
        // Fetch data only when expanding and if it hasn't been fetched/attempted yet
        if (expanded && loadingStatus === 'idle') {
            fetchDetails();
        }
    };


    const renderDetailsContent = () => {
        switch (loadingStatus) {
            case 'loading':
                return (
                    <Box>
                        {/* Use Skeletons matching the expected content */}
                        <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="60%" />
                        {/* Add more skeletons if expecting more data lines */}
                    </Box>
                );
            case 'success':
                if (!detailsData) {
                    return <Typography variant="body2">It may take a few minutes for odin.fun to assign a deposit address, try again in a couple more minutes</Typography>;
                }
                return (
                    <Box>
                        <dl>
                            <dt>
                                <Typography variant="body2">
                                    <strong>Balance:</strong>
                                </Typography>
                            </dt>
                            <dd>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {detailsData.balance ? transformToBTC(detailsData.balance) : 'N/A'}
                                </Typography>
                            </dd>
                            <dt>
                                <Typography variant="body2">
                                    <strong>Deposit Address:</strong>
                                </Typography>
                            </dt>
                            <dd>
                                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>
                                    {detailsData?.depositAddress ?? 'N/A'}
                                </Typography>
                            </dd>
                            <dt>
                                <Typography variant="body2" sx={{ color: showPrivateKey ? 'error.main' : 'white' }}>
                                    <strong>Private Key:</strong>
                                </Typography>
                            </dt>
                            <dd>
                                {!showPrivateKey ? (
                                    <Button variant="outlined" size="small" onClick={handleTogglePrivateKeyVisibility}>
                                        Reveal
                                    </Button>
                                ) : (
                                    <>
                                        <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>
                                            {wallet.privateKey}
                                        </Typography>
                                        <Button variant="outlined" size="small" onClick={handleTogglePrivateKeyVisibility}>
                                            Hide
                                        </Button>
                                    </>
                                )}
                                {showPrivateKey && (
                                    <Typography variant="caption" color="error.main">
                                        Be cautious when revealing your private key.
                                    </Typography>
                                )}
                            </dd>
                        </dl>
                    </Box>
                );
            case 'error':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.light' }}>
                        <ErrorOutlineIcon sx={{ mr: 1 }} fontSize="small" />
                        <Typography variant="body2">
                            {error || 'Could not load details.'}
                        </Typography>
                    </Box>
                );
            case 'idle':
            default:
                // Should not typically be visible when expanded due to fetch trigger logic
                return <Typography variant="body2" sx={{ color: 'text.disabled' }}>Expand to load details...</Typography>;
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };
    return (
        <>
            <Card
                sx={{
                    mb: 2, // Margin bottom between cards
                    // bgcolor: 'background.paper', // Use theme's paper background
                    borderRadius: 2,
                    boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.2)', // Subtle shadow
                    overflow: 'hidden', // Prevent AccordionDetails background bleeding past rounded corners
                    width: '100%', // Full width of the parent container
                    border: '1px solid rgba(255, 255, 255, 0.12)', // Border color
                    '&:hover': {
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.3)', // Shadow on hover
                    },
                }}
            >
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: 'primary.main' }}
                            alt='User Avatar'
                            src={`
https://images.odin.fun/user/${ wallet.userId }?timestamp=${ (new Date()).getTime() }`}

                        />

                    }
                    action={
                        <IconButton
                            aria-label="delete wallet"
                            onClick={handleDelete}
                            sx={{ color: 'error.light' }} // Use theme error color for delete
                        >
                            <DeleteIcon />
                        </IconButton>
                    }
                    title={
                        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            {wallet.name}
                        </Typography>
                    }
                    sx={{ pb: 0 }} // Remove bottom padding if content follows directly
                />
                <CardContent sx={{ pt: 1 }}>
                    <Button
                        onClick={() => window.open(`/users/${ wallet.userId }`)} // Navigate to wallet details

                    >
                        <Typography
                            variant="body2"
                            sx={{
                                // color: 'text.secondary',
                                wordBreak: 'break-all', // Ensure long addresses wrap
                                mb: 2, // Margin below address before accordion,
                                textTransform: 'none', // Prevent uppercase transformation
                            }}
                        >
                            {wallet.userId}
                        </Typography>
                    </Button>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography>
                            <strong>Wallet type:</strong> {wallet.type}
                        </Typography>

                        <Button variant="outlined" onClick={handleClickOpen}>
                            Withdraw Funds
                        </Button>
                    </Box>

                    {/* Accordion for More Details - Now Controlled */}
                    <Accordion
                        expanded={isExpanded} // Control expansion state
                        onChange={handleAccordionChange} // Handle expansion change to trigger fetch
                        sx={{
                            bgcolor: 'transparent', // Make accordion blend in
                            boxShadow: 'none', // Remove default shadow
                            '&:before': { display: 'none' }, // Remove top border
                            borderTop: '1px solid rgba(255, 255, 255, 0.12)', // Add subtle top border
                            marginTop: '16px', // Add space above accordion
                            marginLeft: '-16px', // Align with CardContent padding start
                            marginRight: '-16px', // Align with CardContent padding end
                            // Remove bottom margin if it's the last element visually
                            '&.MuiAccordion-root:last-of-type': {
                                marginBottom: '-16px', // Counteract CardContent padding bottom
                            }
                        }}
                        disableGutters // Remove padding around accordion
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon style={{ color: "white" }} />}
                            aria-controls={`panel-${ wallet.id }-content`}
                            id={`panel-${ wallet.id }-header`}
                            sx={{
                                minHeight: '48px', // Standard height
                                '& .MuiAccordionSummary-content': { // Target content spacing
                                    margin: '12px 0'
                                },
                                paddingLeft: '16px', // Restore padding inside accordion
                                paddingRight: '16px',
                            }}
                        >
                            <Typography variant="body2">
                                {isExpanded ? 'Hide Details' : 'Show Details'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                bgcolor: '#2c2c2c', // Slightly different background for details area
                                padding: '16px', // Restore padding inside accordion
                                // Ensure bottom corners are rounded (applied by Card's overflow:hidden now)
                                // borderBottomLeftRadius: '8px',
                                // borderBottomRightRadius: '8px',
                            }}
                        >
                            {/* Render content based on loading status */}
                            {renderDetailsContent()}
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
            <WithdrawDialog open={open} setOpen={setOpen} wallet={wallet} />
        </>
    );
}

export default React.memo(WalletCard);